/**
 * Cliente centralizado para los endpoints /api/me/* (Engagement: favoritos, planes, listas, recomendaciones).
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ---------- Favorites ----------
export const fetchFavorites = () =>
  axios.get(`${API_URL}/api/me/favorites`, { headers: authHeaders() }).then(r => r.data);

export const fetchFavoriteIds = () =>
  axios.get(`${API_URL}/api/me/favorites/ids`, { headers: authHeaders() }).then(r => r.data);

export const addFavorite = (eventId) =>
  axios.post(`${API_URL}/api/me/favorites/${eventId}`, {}, { headers: authHeaders() }).then(r => r.data);

export const removeFavorite = (eventId) =>
  axios.delete(`${API_URL}/api/me/favorites/${eventId}`, { headers: authHeaders() }).then(r => r.data);

// ---------- Plans ----------
export const fetchPlans = () =>
  axios.get(`${API_URL}/api/me/plans`, { headers: authHeaders() }).then(r => r.data);

export const fetchPlanIds = () =>
  axios.get(`${API_URL}/api/me/plans/ids`, { headers: authHeaders() }).then(r => r.data);

export const setPlan = (eventId, status = 'going', reminderAt = null) =>
  axios.post(`${API_URL}/api/me/plans/${eventId}`,
    { status, reminder_at: reminderAt },
    { headers: authHeaders() }
  ).then(r => r.data);

export const removePlan = (eventId) =>
  axios.delete(`${API_URL}/api/me/plans/${eventId}`, { headers: authHeaders() }).then(r => r.data);

// ---------- Lists ----------
export const fetchLists = () =>
  axios.get(`${API_URL}/api/me/lists`, { headers: authHeaders() }).then(r => r.data);

export const fetchListDetail = (listId) =>
  axios.get(`${API_URL}/api/me/lists/${listId}`, { headers: authHeaders() }).then(r => r.data);

export const createList = (data) =>
  axios.post(`${API_URL}/api/me/lists`, data, { headers: authHeaders() }).then(r => r.data);

export const updateList = (listId, data) =>
  axios.patch(`${API_URL}/api/me/lists/${listId}`, data, { headers: authHeaders() }).then(r => r.data);

export const deleteList = (listId) =>
  axios.delete(`${API_URL}/api/me/lists/${listId}`, { headers: authHeaders() }).then(r => r.data);

export const addEventToList = (listId, eventId, note = null) =>
  axios.post(`${API_URL}/api/me/lists/${listId}/items`,
    { event_id: eventId, note },
    { headers: authHeaders() }
  ).then(r => r.data);

export const removeEventFromList = (listId, eventId) =>
  axios.delete(`${API_URL}/api/me/lists/${listId}/items/${eventId}`, { headers: authHeaders() }).then(r => r.data);

export const joinListByToken = (shareToken) =>
  axios.post(`${API_URL}/api/me/lists/join/${shareToken}`, {}, { headers: authHeaders() }).then(r => r.data);

// ---------- Recommendations ----------
export const fetchRecommendations = (limit = 12) =>
  axios.get(`${API_URL}/api/me/recommendations`, { params: { limit }, headers: authHeaders() }).then(r => r.data);

export const trackUserView = (eventId) =>
  axios.post(`${API_URL}/api/me/track/view/${eventId}`, {}, { headers: authHeaders() }).then(r => r.data).catch(() => null);


// ---------- Gamification: Stats, Preferences, Calendar ----------
export const fetchMyStats = () =>
  axios.get(`${API_URL}/api/me/stats`, { headers: authHeaders() }).then(r => r.data);

export const fetchMyPreferences = () =>
  axios.get(`${API_URL}/api/me/preferences`, { headers: authHeaders() }).then(r => r.data);

export const updateMyPreferences = (data) =>
  axios.patch(`${API_URL}/api/me/preferences`, data, { headers: authHeaders() }).then(r => r.data);

export const uploadMyAvatar = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return axios.post(`${API_URL}/api/uploads/user-avatar`, fd, {
    headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const deleteMyAvatar = () =>
  axios.delete(`${API_URL}/api/uploads/user-avatar`, { headers: authHeaders() }).then(r => r.data);

export const myCalendarIcsUrl = () => {
  // Note: backend requires Bearer header, so we fetch and trigger download client-side
  return `${API_URL}/api/me/calendar.ics`;
};

export const downloadMyCalendar = async () => {
  const res = await axios.get(`${API_URL}/api/me/calendar.ics`, {
    headers: authHeaders(),
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plancity-mis-planes.ics';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ---------- Public profile ----------
export const fetchPublicProfile = (username) =>
  axios.get(`${API_URL}/api/profiles/${encodeURIComponent(username)}`).then(r => r.data);

// ---------- Invites ("Voy con...") ----------
export const createInvite = (eventId) =>
  axios.post(`${API_URL}/api/invites/${eventId}`, {}, { headers: authHeaders() }).then(r => r.data);

export const fetchInvite = (token) =>
  axios.get(`${API_URL}/api/invites/${token}`).then(r => r.data);

export const acceptInvite = (token) =>
  axios.post(`${API_URL}/api/invites/${token}/accept`, {}, { headers: authHeaders() }).then(r => r.data);
